import mixpanel from 'mixpanel-browser';

const CURRENT_USER_KEY = 'current_user';

interface MixpanelUser {
  id?: number;
  email?: string;
  name?: string;
}

/**
 * Identify the user in Mixpanel so all subsequent events and sessions
 * are attributed to them. Also sets profile properties ($email, $name).
 */
export function identifyUser(user: MixpanelUser): void {
  try {
    if (user.email) {
      mixpanel.identify(user.email);
      mixpanel.people.set({
        $email: user.email,
        $name: user.name || user.email,
        ...(user.id != null && { userId: user.id }),
      });
    }
  } catch {
    // Silently fail if Mixpanel is unavailable
  }
}

/**
 * Reset Mixpanel identity on logout so future events are anonymous.
 */
export function resetUser(): void {
  try {
    mixpanel.reset();
  } catch {
    // Silently fail if Mixpanel is unavailable
  }
}

/**
 * Re-identify a returning user from localStorage on app boot.
 * Should be called once after `mixpanel.init()`.
 */
export function identifyFromStorage(): void {
  try {
    const raw = localStorage.getItem(CURRENT_USER_KEY);
    if (!raw) return;
    const user = JSON.parse(raw) as MixpanelUser;
    identifyUser(user);
  } catch {
    // Silently fail if storage or parsing fails
  }
}

/* ------------------------------------------------------------------ */
/*  Event Tracking (#1)                                                */
/* ------------------------------------------------------------------ */

/**
 * Track a custom event with optional properties.
 * Wraps `mixpanel.track` with error handling so callers don't need try/catch.
 */
export function trackEvent(event: string, properties?: Record<string, unknown>): void {
  try {
    mixpanel.track(event, properties);
  } catch {
    // Silently fail
  }
}

/* ------------------------------------------------------------------ */
/*  Super Properties (#3)                                              */
/* ------------------------------------------------------------------ */

/**
 * Register super properties that are sent with every subsequent event.
 * Use this to attach session-level context (e.g. current domain).
 */
export function registerSuperProperties(properties: Record<string, unknown>): void {
  try {
    mixpanel.register(properties);
  } catch {
    // Silently fail
  }
}

/* ------------------------------------------------------------------ */
/*  Timed Events (#5)                                                  */
/* ------------------------------------------------------------------ */

/**
 * Start timing an event. Call `trackEvent(eventName)` later to
 * automatically include a `$duration` property (in seconds).
 */
export function startTimedEvent(event: string): void {
  try {
    mixpanel.time_event(event);
  } catch {
    // Silently fail
  }
}

